import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { queryKeys } from '@/config';
import { TO_ASSIGN_KEY } from '@/features/shared/step-assign';
import { invalidateStepQueries } from '@/features/shared/step-queries';
import { getSocket } from '@/lib';
import { useNotificationsStore } from '@/stores';

export function useWebSocketEvents() {
  const queryClient = useQueryClient();
  const incrementUnread = useNotificationsStore((s) => s.increment);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleStepStatusChanged = () => invalidateStepQueries(queryClient);

    const handleEpisodeStatusChanged = () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shows.all });
    };

    const handleNewNotification = () => {
      incrementUnread();
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      // "Za dodelu" has no websocket event of its own: a step reaches the
      // assigner's queue together with the notification that announces it.
      queryClient.invalidateQueries({ queryKey: TO_ASSIGN_KEY });
    };

    const handleCommentAdded = (data: { episodeId: string }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.episodes.activity(data.episodeId) });
    };

    socket.on('step:status-changed', handleStepStatusChanged);
    socket.on('step:available', handleStepStatusChanged);
    socket.on('step:accepted', handleStepStatusChanged);
    socket.on('step:completed', handleStepStatusChanged);
    socket.on('step:rejected', handleStepStatusChanged);
    socket.on('step:show-stopper', handleStepStatusChanged);
    socket.on('episode:status-changed', handleEpisodeStatusChanged);
    socket.on('notification:new', handleNewNotification);
    socket.on('episode:comment-added', handleCommentAdded);

    return () => {
      socket.off('step:status-changed', handleStepStatusChanged);
      socket.off('step:available', handleStepStatusChanged);
      socket.off('step:accepted', handleStepStatusChanged);
      socket.off('step:completed', handleStepStatusChanged);
      socket.off('step:rejected', handleStepStatusChanged);
      socket.off('step:show-stopper', handleStepStatusChanged);
      socket.off('episode:status-changed', handleEpisodeStatusChanged);
      socket.off('notification:new', handleNewNotification);
      socket.off('episode:comment-added', handleCommentAdded);
    };
  }, [queryClient, incrementUnread]);
}
